'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

import Link from 'next/link';

interface ReportingFramework {
  id: string;
  name: string;
  description: string;
  icon: string;
  features: string[];
  applicability: string;
  timeline: string;
}

const ESGReportingInteractive = () => {
  const [selectedFramework, setSelectedFramework] = useState<string>('all');

  const frameworks: ReportingFramework[] = [
    {
      id: 'brsr-lite',
      name: 'BRSR Lite',
      description: 'Simplified Business Responsibility and Sustainability Reporting for smaller enterprises and value chain partners aligned with Net Zero agenda.',
      icon: 'DocumentTextIcon',
      features: [
        'Simplified disclosure requirements',
        'Value chain partner alignment',
        'Net Zero pathway integration',
        'Automated data collection',
        'Stakeholder engagement tools',
      ],
      applicability: 'SMEs and Value Chain Partners',
      timeline: 'Annual Reporting',
    },
    {
      id: 'csrd',
      name: 'CSRD (Corporate Sustainability Reporting Directive)',
      description: 'European market compliance framework with comprehensive sustainability disclosure requirements for organizations operating in EU markets.',
      icon: 'GlobeAltIcon',
      features: [
        'Double materiality assessment',
        'ESRS standards alignment',
        'Digital tagging (XBRL)',
        'Third-party assurance',
        'Value chain reporting',
      ],
      applicability: 'EU Market Operations',
      timeline: 'Annual with Phased Implementation',
    },
    {
      id: 'ghg-protocol',
      name: 'GHG Protocol',
      description: 'Standardized emission calculation methodologies for comprehensive greenhouse gas accounting across Scope 1, 2, and 3 emissions.',
      icon: 'BeakerIcon',
      features: [
        'Scope 1, 2, 3 calculation',
        'Organizational boundaries',
        'Emission factors database',
        'Inventory management',
        'Verification support',
      ],
      applicability: 'All Organizations',
      timeline: 'Annual with Quarterly Updates',
    },
    {
      id: 'sbti',
      name: 'SBTi for Value Chain Partners',
      description: 'Science-Based Targets initiative framework enabling value chain partners to set and validate emission reduction targets aligned with climate science.',
      icon: 'ChartBarIcon',
      features: [
        'Target setting methodology',
        'Validation and tracking',
        'Value chain engagement',
        'Progress monitoring',
        'Certification support',
      ],
      applicability: 'Value Chain Partners',
      timeline: '5-10 Year Targets',
    },
    {
      id: 'brsr-top-500',
      name: 'BRSR for Top 500 Companies',
      description: 'Comprehensive SEBI-mandated disclosure framework for India\'s top 500 listed companies with detailed ESG metrics and performance indicators.',
      icon: 'BuildingOfficeIcon',
      features: [
        'Section A: General disclosures',
        'Section B: Management & process',
        'Section C: Principle-wise performance',
        'Leadership indicators',
        'Automated compliance check',
      ],
      applicability: 'Top 500 Listed Companies (India)',
      timeline: 'Annual SEBI Filing',
    },
    {
      id: 'net-zero-roadmap',
      name: 'Net Zero Roadmap',
      description: 'Strategic planning tool for achieving carbon neutrality with milestone tracking for 2030, 2047, and 2074 targets.',
      icon: 'MapIcon',
      features: [
        'Baseline assessment',
        'Target setting (2030/2047/2074)',
        'Decarbonization pathways',
        'Investment planning',
        'Progress tracking dashboard',
      ],
      applicability: 'All Organizations',
      timeline: 'Long-term Strategic Plan',
    },
    {
      id: 'fibac',
      name: 'FIBAC Reporting',
      description: 'Banking sector-specific sustainability framework addressing financial impact assessment and climate risk integration.',
      icon: 'BanknotesIcon',
      features: [
        'Financed emissions calculation',
        'Climate risk assessment',
        'Green lending metrics',
        'Portfolio alignment',
        'Regulatory compliance',
      ],
      applicability: 'Banking & Financial Sector',
      timeline: 'Annual with Quarterly Reviews',
    },
    {
      id: 'scope-3',
      name: 'Scope 3 Assessment',
      description: 'Comprehensive value chain emission tracking framework with supplier engagement tools and category-wise analysis.',
      icon: 'ArrowsRightLeftIcon',
      features: [
        'Category-wise tracking (15 categories)',
        'Supplier engagement platform',
        'Data collection automation',
        'Hotspot analysis',
        'Reduction strategies',
      ],
      applicability: 'All Organizations',
      timeline: 'Annual with Ongoing Monitoring',
    },
  ];

  const filteredFrameworks = selectedFramework === 'all' 
    ? frameworks 
    : frameworks.filter(f => f.id === selectedFramework);

  return (
    <main className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 via-surface to-secondary/10 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto space-y-6">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-full mb-4">
              <Icon name="ShieldCheckIcon" size={20} className="text-accent" />
              <span className="text-sm font-semibold text-accent">Regulatory Compliance & Sustainability Frameworks</span>
            </div>
            
            <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-primary leading-tight">
              ESG Reporting Solutions
            </h1>
            
            <p className="text-xl md:text-2xl text-text-secondary max-w-3xl mx-auto">
              Comprehensive platform for navigating complex ESG reporting requirements with expert guidance and automated compliance tools
            </p>

            <div className="flex flex-wrap justify-center gap-4 pt-6">
              <Link
                href="/get-started"
                className="px-8 py-3 bg-conversion text-conversion-foreground font-heading font-semibold rounded-lg hover:bg-conversion/90 shadow-natural hover:shadow-natural-lg transition-all duration-300"
              >
                Schedule Consultation
              </Link>
              <a
                href="https://wa.me/919901503669"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3 bg-card text-text-primary font-heading font-semibold rounded-lg border-2 border-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300"
              >
                Contact Expert
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Framework Filter */}
      <section className="py-8 bg-surface sticky top-16 z-40 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => setSelectedFramework('all')}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                selectedFramework === 'all' ?'bg-primary text-primary-foreground shadow-natural' :'bg-card text-text-secondary hover:bg-accent/10 border border-border'
              }`}
            >
              All Frameworks
            </button>
            {frameworks.map((framework) => (
              <button
                key={framework.id}
                onClick={() => setSelectedFramework(framework.id)}
                className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                  selectedFramework === framework.id
                    ? 'bg-primary text-primary-foreground shadow-natural'
                    : 'bg-card text-text-secondary hover:bg-accent/10 border border-border'
                }`}
              >
                {framework.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Frameworks Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {filteredFrameworks.map((framework) => (
              <div
                key={framework.id}
                className="bg-card rounded-2xl p-8 border border-border hover:border-primary transition-all duration-300 hover:shadow-natural-lg"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon name={framework.icon} size={28} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-2xl text-text-primary mb-2">
                      {framework.name}
                    </h3>
                    <p className="text-sm text-text-secondary">
                      {framework.applicability} • {framework.timeline}
                    </p>
                  </div>
                </div>

                <p className="text-text-secondary mb-6 leading-relaxed">
                  {framework.description}
                </p>

                <div className="space-y-3 mb-6">
                  <h4 className="font-semibold text-text-primary mb-3">Key Features:</h4>
                  {framework.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <Icon name="CheckCircleIcon" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-text-secondary">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="flex space-x-4">
                  <Link
                    href="/get-started"
                    className="flex-1 px-6 py-3 bg-primary text-primary-foreground font-semibold text-center rounded-lg hover:bg-primary/90 transition-colors duration-300"
                  >
                    Get Started
                  </Link>
                  <button className="px-6 py-3 bg-surface text-text-primary font-semibold rounded-lg border border-border hover:bg-accent/10 transition-colors duration-300">
                    Learn More
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Compliance Matrix */}
      <section className="py-16 md:py-24 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
              Compliance Matrix
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Interactive framework comparison to identify your reporting requirements
            </p>
          </div>

          <div className="bg-card rounded-2xl p-8 border border-border overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-4 px-4 font-semibold text-text-primary">Framework</th>
                  <th className="text-center py-4 px-4 font-semibold text-text-primary">Region</th>
                  <th className="text-center py-4 px-4 font-semibold text-text-primary">Mandatory</th>
                  <th className="text-center py-4 px-4 font-semibold text-text-primary">Timeline</th>
                </tr>
              </thead>
              <tbody>
                {frameworks.map((framework) => (
                  <tr key={framework.id} className="border-b border-border hover:bg-surface transition-colors">
                    <td className="py-4 px-4 font-medium text-text-primary">{framework.name}</td>
                    <td className="py-4 px-4 text-center text-text-secondary">{framework.applicability}</td>
                    <td className="py-4 px-4 text-center">
                      {framework.id === 'brsr-top-500' || framework.id === 'csrd' ? (
                        <Icon name="CheckCircleIcon" size={24} className="text-accent mx-auto" />
                      ) : (
                        <Icon name="MinusCircleIcon" size={24} className="text-text-secondary mx-auto" />
                      )}
                    </td>
                    <td className="py-4 px-4 text-center text-text-secondary">{framework.timeline}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary/10 to-secondary/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-6">
            Ready to Navigate ESG Compliance?
          </h2>
          <p className="text-xl text-text-secondary mb-8 max-w-2xl mx-auto">
            Get expert guidance tailored to your industry and regulatory requirements
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              href="/get-started"
              className="px-8 py-4 bg-conversion text-conversion-foreground font-heading font-semibold text-lg rounded-lg hover:bg-conversion/90 shadow-natural hover:shadow-natural-lg transition-all duration-300"
            >
              Schedule Free Consultation
            </Link>
            <a
              href={`mailto:dr.nupur@gencarbon.in`}
              className="px-8 py-4 bg-card text-text-primary font-heading font-semibold text-lg rounded-lg border-2 border-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300"
            >
              Email Us
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ESGReportingInteractive;